(() => {
  if (window.ArcticHeaderSearch) return;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const DEFAULT_HISTORY_KEY = 'arctic-recent-searches';
  const HISTORY_LIMIT = 3;
  const HISTORY_EVENT = 'arctic:search-history-change';
  const controllers = new Set();
  const controllerByHeader = new WeakMap();

  const normalizeQuery = (value) => String(value || '').trim().replace(/\s+/g, ' ');

  const readHistory = (key) => {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || '[]');
      if (!Array.isArray(value)) return [];

      const seen = new Set();
      return value.reduce((queries, entry) => {
        const query = normalizeQuery(entry);
        const identity = query.toLocaleLowerCase();
        if (!query || seen.has(identity) || queries.length >= HISTORY_LIMIT) return queries;
        seen.add(identity);
        queries.push(query);
        return queries;
      }, []);
    } catch (_error) {
      return [];
    }
  };

  const writeHistory = (key, queries) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(queries.slice(0, HISTORY_LIMIT)));
      return true;
    } catch (_error) {
      return false;
    }
  };

  const addToHistory = (key, value) => {
    const query = normalizeQuery(value);
    if (!query) return [];

    const identity = query.toLocaleLowerCase();
    const queries = [
      query,
      ...readHistory(key).filter((entry) => entry.toLocaleLowerCase() !== identity)
    ].slice(0, HISTORY_LIMIT);

    writeHistory(key, queries);
    window.dispatchEvent(new CustomEvent(HISTORY_EVENT, { detail: { key } }));
    return queries;
  };

  const searchUrlFor = (form, query) => {
    const url = new URL(form.getAttribute('action') || window.location.href, window.location.href);

    form.querySelectorAll('input[type="hidden"][name]').forEach((input) => {
      if (input.disabled || !input.name || input.name === 'q') return;
      url.searchParams.set(input.name, input.value);
    });

    url.searchParams.set('q', query);
    return url.toString();
  };

  const setInteractive = (element, interactive) => {
    if (!(element instanceof HTMLElement)) return;
    element.inert = !interactive;
    element.setAttribute('aria-hidden', String(!interactive));
  };

  class HeaderSearchController {
    constructor(header) {
      this.header = header;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.desktopForm = header.querySelector('[data-header-search-form]');
      this.desktopInput = header.querySelector('[data-header-search-input]');
      this.desktopToggle = header.querySelector('[data-header-search-toggle]');
      this.desktopClose = header.querySelector('[data-header-search-close]');
      this.mobilePanel = header.querySelector('[data-mobile-search-panel]');
      this.mobileForm = header.querySelector('[data-mobile-search-form]');
      this.mobileInput = header.querySelector('[data-mobile-search-input]');
      this.mobileToggle = header.querySelector('[data-mobile-search-toggle]');
      this.mobileClose = header.querySelector('[data-mobile-search-close]');
      this.recentSearches = header.querySelector('[data-recent-searches]');
      this.recentList = header.querySelector('[data-recent-search-list]');
      this.historyKey =
        header.dataset.searchHistoryKey
        || header.querySelector('[data-search-history-key]')?.dataset.searchHistoryKey
        || DEFAULT_HISTORY_KEY;
      this.desktopOpen = false;
      this.mobileOpen = false;
      this.desktopStateTimer = 0;
      this.mobileStateTimer = 0;
      if (this.desktopForm?.id) {
        this.desktopToggle?.setAttribute('aria-controls', this.desktopForm.id);
      }
      if (this.mobilePanel?.id) {
        this.mobileToggle?.setAttribute('aria-controls', this.mobilePanel.id);
      }
      this.bind();
      this.resetForBreakpoint();
      this.renderHistory();
    }

    bind() {
      this.desktopToggle?.addEventListener('click', (event) => {
        if (!this.desktopQuery.matches) return;
        event.preventDefault();

        if (!this.desktopOpen) {
          this.openDesktop();
          return;
        }

        const query = normalizeQuery(this.desktopInput?.value);
        if (query) this.desktopForm?.requestSubmit();
        else this.desktopInput?.focus({ preventScroll: true });
      }, { signal: this.signal });

      this.desktopClose?.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeDesktop({ restoreFocus: true });
      }, { signal: this.signal });

      this.mobileToggle?.addEventListener('click', (event) => {
        if (this.desktopQuery.matches) return;
        event.preventDefault();
        if (this.mobileOpen) this.closeMobile({ restoreFocus: true });
        else this.openMobile();
      }, { signal: this.signal });

      this.mobileClose?.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeMobile({ restoreFocus: true });
      }, { signal: this.signal });

      this.desktopForm?.addEventListener('submit', (event) => {
        this.handleSubmit(event, this.desktopInput);
      }, { signal: this.signal });

      this.mobileForm?.addEventListener('submit', (event) => {
        this.handleSubmit(event, this.mobileInput);
      }, { signal: this.signal });

      document.addEventListener('pointerdown', (event) => {
        this.handleOutsidePointer(event);
      }, { signal: this.signal });

      document.addEventListener('click', (event) => {
        const opener = event.target instanceof Element
          ? event.target.closest('[data-dialog-open], [data-cart-open]')
          : null;
        if (!opener) return;
        this.closeDesktop({ restoreFocus: false });
        this.closeMobile({ restoreFocus: false });
      }, { signal: this.signal });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        if (this.desktopQuery.matches && this.desktopOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.closeDesktop({ restoreFocus: true });
        } else if (!this.desktopQuery.matches && this.mobileOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.closeMobile({ restoreFocus: true });
        }
      }, { signal: this.signal });

      this.desktopQuery.addEventListener('change', () => {
        this.resetForBreakpoint();
      }, { signal: this.signal });

      window.addEventListener(HISTORY_EVENT, (event) => {
        if (event.detail?.key === this.historyKey) this.renderHistory();
      }, { signal: this.signal });

      window.addEventListener('storage', (event) => {
        if (event.key === this.historyKey) this.renderHistory();
      }, { signal: this.signal });
    }

    handleSubmit(event, input) {
      const query = normalizeQuery(input?.value);
      if (!query) {
        event.preventDefault();
        input?.focus({ preventScroll: true });
        return;
      }

      input.value = query;
      addToHistory(this.historyKey, query);
    }

    handleOutsidePointer(event) {
      if (!(event.target instanceof Node)) return;

      if (this.desktopOpen) {
        const insideDesktopSearch = this.desktopForm?.contains(event.target)
          || this.desktopToggle?.contains(event.target);
        if (!insideDesktopSearch) this.closeDesktop({ restoreFocus: false });
      }

      if (this.mobileOpen) {
        const insideMobileSearch = this.mobilePanel?.contains(event.target)
          || this.mobileToggle?.contains(event.target);
        if (!insideMobileSearch) this.closeMobile({ restoreFocus: false });
      }
    }

    setSurfaceState(surface, state, open) {
      if (!(surface instanceof HTMLElement)) return;
      surface.dataset.searchState = state;
      surface.dataset.searchOpen = String(open);
      setInteractive(surface, open);
    }

    openDesktop() {
      if (!this.desktopForm || this.desktopOpen || !this.desktopQuery.matches) return;
      this.closeMobile({ restoreFocus: false });
      this.desktopOpen = true;
      window.clearTimeout(this.desktopStateTimer);
      this.header.dataset.searchOpen = 'true';
      this.desktopToggle?.setAttribute('aria-expanded', 'true');
      this.setSurfaceState(this.desktopForm, 'opening', true);

      window.requestAnimationFrame(() => {
        if (!this.desktopOpen) return;
        this.desktopForm.dataset.searchState = 'open';
        this.desktopInput?.focus({ preventScroll: true });
      });
    }

    closeDesktop({ restoreFocus = false, immediate = false } = {}) {
      if (!this.desktopForm) return;
      const wasOpen = this.desktopOpen;
      this.desktopOpen = false;
      window.clearTimeout(this.desktopStateTimer);
      this.header.dataset.searchOpen = 'false';
      this.desktopToggle?.setAttribute('aria-expanded', 'false');
      this.setSurfaceState(this.desktopForm, immediate ? 'closed' : 'closing', false);

      if (!immediate) {
        this.desktopStateTimer = window.setTimeout(() => {
          if (!this.desktopOpen) this.desktopForm.dataset.searchState = 'closed';
        }, 500);
      }

      if (wasOpen && restoreFocus) this.desktopToggle?.focus({ preventScroll: true });
    }

    openMobile() {
      if (!this.mobilePanel || this.mobileOpen || this.desktopQuery.matches) return;
      this.closeDesktop({ restoreFocus: false });
      this.mobileOpen = true;
      window.clearTimeout(this.mobileStateTimer);
      this.mobilePanel.hidden = false;
      this.header.dataset.mobileSearchOpen = 'true';
      this.mobileToggle?.setAttribute('aria-expanded', 'true');
      this.mobilePanel.dataset.searchState = 'closed';
      this.mobilePanel.dataset.searchOpen = 'false';
      setInteractive(this.mobilePanel, true);
      this.renderHistory();

      // Commit the collapsed 0fr state before starting the expansion.
      this.mobilePanel.getBoundingClientRect();

      window.requestAnimationFrame(() => {
        if (!this.mobileOpen) return;
        this.mobilePanel.dataset.searchState = 'opening';
        this.mobilePanel.dataset.searchOpen = 'true';
        this.mobileInput?.focus({ preventScroll: true });

        window.requestAnimationFrame(() => {
          if (this.mobileOpen) this.mobilePanel.dataset.searchState = 'open';
        });
      });
    }

    closeMobile({ restoreFocus = false, immediate = false } = {}) {
      if (!this.mobilePanel) return;
      const wasOpen = this.mobileOpen;
      this.mobileOpen = false;
      window.clearTimeout(this.mobileStateTimer);
      this.header.dataset.mobileSearchOpen = 'false';
      this.mobileToggle?.setAttribute('aria-expanded', 'false');
      this.setSurfaceState(this.mobilePanel, immediate ? 'closed' : 'closing', false);

      if (immediate) {
        this.mobilePanel.hidden = true;
      } else {
        this.mobileStateTimer = window.setTimeout(() => {
          if (!this.mobileOpen) {
            this.mobilePanel.dataset.searchState = 'closed';
            this.mobilePanel.hidden = true;
          }
        }, 500);
      }

      if (wasOpen && restoreFocus) this.mobileToggle?.focus({ preventScroll: true });
    }

    resetForBreakpoint() {
      this.closeDesktop({ restoreFocus: false, immediate: true });
      this.closeMobile({ restoreFocus: false, immediate: true });
      this.header.dataset.searchMode = this.desktopQuery.matches ? 'desktop' : 'mobile';
    }

    renderHistory() {
      if (!this.recentList || !this.recentSearches || !this.mobileForm) return;
      const queries = readHistory(this.historyKey);
      this.recentList.replaceChildren();

      queries.forEach((query) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        item.className = 'mobile-search__recent-item';
        link.className = 'mobile-search__recent-link';
        link.href = searchUrlFor(this.mobileForm, query);
        link.textContent = query;
        link.dataset.recentSearchQuery = query;
        link.addEventListener('click', () => addToHistory(this.historyKey, query), {
          signal: this.signal
        });
        item.append(link);
        this.recentList.append(item);
      });

      this.recentSearches.hidden = queries.length === 0;
      this.recentSearches.setAttribute('aria-hidden', String(queries.length === 0));
    }

    destroy() {
      window.clearTimeout(this.desktopStateTimer);
      window.clearTimeout(this.mobileStateTimer);
      this.abortController.abort();
      controllers.delete(this);
      controllerByHeader.delete(this.header);
    }
  }

  const cleanupDisconnected = () => {
    controllers.forEach((controller) => {
      if (!controller.header.isConnected) controller.destroy();
    });
  };

  const initialize = (scope = document) => {
    cleanupDisconnected();

    const headers = [];
    if (scope instanceof Element && scope.matches('[data-site-header]')) headers.push(scope);
    if (scope instanceof Document || scope instanceof DocumentFragment || scope instanceof Element) {
      headers.push(...scope.querySelectorAll('[data-site-header]'));
    }

    headers.forEach((header) => {
      if (controllerByHeader.has(header)) return;
      const controller = new HeaderSearchController(header);
      controllerByHeader.set(header, controller);
      controllers.add(controller);
    });
  };

  const closeAll = ({ restoreFocus = false } = {}) => {
    controllers.forEach((controller) => {
      controller.closeDesktop({ restoreFocus });
      controller.closeMobile({ restoreFocus });
    });
  };

  window.ArcticHeaderSearch = { initialize, closeAll };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-header-search-form], [data-mobile-search-form]')) return;

    const headerSearchForm = document.querySelector('[data-mobile-search-form], [data-header-search-form]');
    if (!(headerSearchForm instanceof HTMLFormElement)) return;

    const submittedUrl = new URL(form.getAttribute('action') || window.location.href, window.location.href);
    const searchUrl = new URL(
      headerSearchForm.getAttribute('action') || window.location.href,
      window.location.href
    );
    if (submittedUrl.origin !== searchUrl.origin || submittedUrl.pathname !== searchUrl.pathname) return;

    const input = form.querySelector('input[type="search"][name="q"], input[name="q"]');
    if (!(input instanceof HTMLInputElement)) return;

    const query = normalizeQuery(input.value);
    if (!query) return;

    const historySource = document.querySelector('[data-search-history-key]');
    const historyKey = historySource?.dataset.searchHistoryKey || DEFAULT_HISTORY_KEY;
    input.value = query;
    addToHistory(historyKey, query);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller) => {
      if (event.target === controller.header || event.target.contains(controller.header)) {
        controller.destroy();
      }
    });
  });
})();
